import { AfterViewInit, Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { BlobType } from '@azure/storage-blob';
import {
    CreateFolder,
    DatasetFileTypeOption,
    FileAction,
    FileColumnKey,
    FileModel,
    ItemsToBeRemovedOptions,
    MEDFileAction,
    MEDFileColumnKeys,
    MedResultItemVM,
    UploadFileItem,
} from '@ca/core/models';
import {
    DialogService,
    ExperimentalFeaturesService,
    FileDownloadService,
    FilePreviewService,
    FileUploadService,
    TrackingService,
    UserNotificationService,
    UserService,
    WindowRef,
} from '@ca/core/services';
import { isConvertedAndHasInvalidValidationStatus } from '@ca/utils';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { FileItem, FileUploader } from 'ng2-file-upload';
import { FileColumn } from 'src/app/core/models/file-column.model';
import { Folder } from 'src/app/core/models/folder.model';
import { ArivisService } from 'src/app/core/services/arivis.service';
import { FileExplorerMode } from 'src/app/core/types/file-explorer-mode.type';
import { FILE_EXPLORER_MODE, UPLOADER_KEY, fileDownloadThresholdInBytes } from '../../constants/constants';
import { FileSizePipe } from '../../pipes/file-size/file-size.pipe';
import { MEDEditStudyIDDialogComponent } from '../MED-edit-study-ID-dialog/MED-edit-study-ID-dialog.component';
import { MEDEditTagsDialogComponent } from '../MED-edit-tags-dialog/MED-edit-tags-dialog.component';
import { TableRow } from './table-row.model';

export const ARCHIVED_FILES_FILENAME = 'my-files';

@UntilDestroy()
@Component({
    selector: 'ca-files-table',
    templateUrl: './files-table.component.html',
    styleUrls: ['./files-table.component.scss'],
})
export class FilesTableComponent implements AfterViewInit {
    @ViewChild('filePreview') filePreviewElement: ElementRef;

    @Input() mode: FileExplorerMode = FILE_EXPLORER_MODE.MODIFICATION;
    @Input() isLoading: boolean;
    @Input() isFileExplorerShown?: string;
    @Input() isSelectedFilesShown?: boolean;
    @Input() blobType: BlobType;
    @Input() folderId?: string;
    @Input() isUploadOfFilesEnabled?: boolean;
    @Input() targetFolderToBeCreated?: CreateFolder;
    @Input() totalNumberOfFiles: number;
    @Input() set items(items: (FileModel | Folder | MedResultItemVM)[]) {
        this.numberOfFiles = 0;
        if (items) {
            this._rows = items.map(item => {
                const row: TableRow = { item, selected: false, isFolder: item instanceof Folder };
                if (!row.isFolder) {
                    const file: FileModel = <FileModel>row.item;
                    file.shareUrl = this.arivisService.generateArivisViewerUrl(file.token);
                    this.numberOfFiles++;
                }
                if (item instanceof FileModel) {
                    row.isConvertedAndHasInvalidValidationStatus = isConvertedAndHasInvalidValidationStatus(
                        item.convertStatus || 'Unknown',
                        item.validationStatus || 'Unknown',
                    );
                }
                return row;
            });
            this.areAllFilesSelected = false;
            this.selectedRows = [];
            this.elementRef?.nativeElement?.scrollTo(0, 0);
        }
    }

    @Input() cancelUpload: FileItem;
    @Input() cancelAllUploads: { cancel: boolean };
    // If undefined, we're not in a dataset. Else, will show the type of the folder we're in (Files or Annotations)
    @Input() datasetFileType?: DatasetFileTypeOption;
    @Input() columns: (FileColumn<FileColumnKey> | FileColumn<MEDFileColumnKeys>)[];
    @Input() actions: (FileAction | MEDFileAction)[];
    @Input() totalRowCount: number;
    @Input() isSharingFilesActivated = false;
    @Input() isAddAllFilesClickButtonShown = false;
    @Input() addAllFilesButtonTooltip = '';
    @Input() uploaderKey: string;
    @Input() myFilesClass = false;
    @Input() stickyHeader = true;
    @Input() showFileStatus = false;
    @Input() truncateFileNameAtWidthOf?: number;
    @Input() allowSameFileNames = true;
    @Input() supportedFileTypes = new Array<string>();
    @Input() allFiles: FileModel[];
    @Input() showEmptyRows = true;
    @Input() showTableHead = true;
    @Input() isDownloadAnnotationsTrackingEventEnabled: boolean = false;
    @Input() isConversionFailedVisible?: boolean;
    @Input() isInvalidFilesVisibile?: boolean;
    @Input() isCZIConversionInProgress?: boolean;
    @Input() isRemoveInvalidFilesDialog?: boolean = false;
    @Input() isAddedFilesVisible?: boolean;
    // If we have a dataset id we'll add it to the annotate request
    @Input() datasetId?: string;
    @Input() isSegmentation?: boolean;
    @Input() isMEDProject?: boolean = false;
    @Input() isFilesPage?: boolean = false;
    @Input() isFilesStatusShown?: boolean = true;
    @Input() isPageSizeModifiable: boolean = false;

    @Output() onAllUploadsCompleted: EventEmitter<void> = new EventEmitter<void>();
    @Output() onUploadCancelled: EventEmitter<void> = new EventEmitter<void>();
    @Output() renameFolder = new EventEmitter<FileModel>();
    @Output() deleteItems = new EventEmitter<ItemsToBeRemovedOptions<FileModel | Folder | MedResultItemVM>>();
    @Output() removeItems = new EventEmitter<ItemsToBeRemovedOptions<FileModel | Folder | MedResultItemVM>>();
    @Output() onClickOnFile = new EventEmitter<FileModel>();
    @Output() onClickOnFolder = new EventEmitter<string>();
    @Output() onOpenFolder = new EventEmitter<string>();
    @Output() addAllFilesClick = new EventEmitter<void>();
    @Output() onFilesAdded = new EventEmitter<UploadFileItem[]>();
    @Output() onUploadCompleted = new EventEmitter<FileModel>();
    @Output() targetFolderCreated = new EventEmitter<Folder>();
    @Output() changeStudyId: EventEmitter<{ studyId: string; fileIDs: string[] }> = new EventEmitter();
    @Output() changeCustomTags: EventEmitter<{ customTags: string[]; fileIDs: string[] }> = new EventEmitter();
    @Output() removeTag: EventEmitter<{ customTags: string[]; fileIDs: string[] }> = new EventEmitter();

    selectedRows: Array<TableRow> = [];
    tableMessages = {
        emptyMessage: this.translateService.instant('MY_WORKSPACE.MY_FILES.NO_FILES_FOUND'),
    };
    isZipInProgress = false;
    isDownloadBusy = false;
    areAllFilesSelected = false;
    numberOfFiles: number = 0;
    fileItemSubMenuOpenIndex: number | null = null;
    folderItemSubMenuOpenIndex: number | null = null;
    uploader: FileUploader | null;
    isCziConversionEnabled = this.experimentalFeaturesService.isCziConversionFeatureEnabled;
    isMEDProjectEnabled = this.experimentalFeaturesService.isMEDProjectActive;

    tags: string[] = [];
    newTagInput: string = '';

    FILE_EXPLORER_MODE = FILE_EXPLORER_MODE;
    Array = Array;
    private _rows: TableRow<FileModel | Folder | MedResultItemVM>[];

    get rows(): TableRow<FileModel | Folder | MedResultItemVM>[] {
        return this._rows;
    }

    get areSomeRowsSelected(): boolean {
        return this.selectedRows.length > 0;
    }

    get tableBannerText(): string {
        return this.getSelectedItemsText(this.selectedRows);
    }

    get selectedFiles(): FileModel[] {
        return this.getFiles(this.selectedRows);
    }

    get selectedFilesAndFolders(): (FileModel | Folder | MedResultItemVM)[] {
        return this.selectedRows.map(r => r.item);
    }

    constructor(
        private arivisService: ArivisService,
        private dialogService: DialogService,
        private elementRef: ElementRef,
        private modalService: NgbModal,
        private experimentalFeaturesService: ExperimentalFeaturesService,
        private fileDownloadService: FileDownloadService,
        private filePreviewService: FilePreviewService,
        private fileSizePipe: FileSizePipe,
        private fileUploadService: FileUploadService,
        private translateService: TranslateService,
        private userNotificationService: UserNotificationService,
        private mixpanelTrackingService: TrackingService,
        private userService: UserService,
        private windowRef: WindowRef,
    ) {}

    ngAfterViewInit(): void {
        this.isCheckboxIncluded();
        // setTimeout to avoid expression has changed after it was checked exception
        setTimeout(() => {
            this.uploader = this.fileUploadService.fetchFromCache(UPLOADER_KEY.MY_FILES_UPLOAD_UPLOADER_KEY);
        }, 500);
    }

    isAtLeastOneArivisPreviewableFileSelected(): boolean {
        return this.filePreviewService.isAtLeastOneArivisPreviewableFileSelected(this.selectedFiles);
    }

    isDownloadMultipleFilesBtnShown(): boolean {
        return this.actions.includes('download') && !!this.selectedRows.find(f => !f.isFolder);
    }

    onSelectAllRowsChange(): void {
        this.areAllFilesSelected = !this.areAllFilesSelected;

        this.selectedRows = this.areAllFilesSelected ? [...this.rows] : [];

        this.rows.forEach((f: TableRow) => {
            f.selected = this.areAllFilesSelected;
        });
    }

    openSelectedFilesInArivis(): void {
        const isDatasetFilesFolder = this.datasetFileType ? this.datasetFileType === DatasetFileTypeOption.FILES : false;

        const previewableFiles = this.selectedFiles.filter((file: FileModel) => this.arivisService.isSupportedByArivis(file.type));

        let url: string;
        if (this.datasetId != null) {
            url = isDatasetFilesFolder
                ? this.arivisService.generateArivisMlViewerUrl(previewableFiles, this.datasetId)
                : this.arivisService.generateArivisMlViewerUrl(previewableFiles);
        } else {
            url = this.arivisService.generateArivisMlViewerUrl(previewableFiles);
        }
        this.windowRef.nativeWindow.open(url, '_blank');
    }

    downloadSelectedFiles(): void {
        if (this.isDownloadAnnotationsTrackingEventEnabled) {
            this.mixpanelTrackingService.trackEvent('download_annotation_started', {
                userId: this.userService.myInfo?.sub,
                datasetId: this.datasetId,
            });
        }

        const files = this.getFiles(this.selectedRows);
        const folders = this.selectedRows.filter(r => r.isFolder);

        if (files.length > 0 && folders.length > 0) {
            this.userNotificationService.showWarning('MY_WORKSPACE.MY_FILES.FOLDERS_ARE_SKIPPED_FROM_DOWNLOAD');
        }

        const totalSize = files.reduce((prev, current) => prev + current.sizeInBytes, 0);

        if (totalSize >= fileDownloadThresholdInBytes) {
            const confirmModal = this.dialogService.openConfirm({
                message: this.translateService.instant('MY_WORKSPACE.MY_FILES.DOWNLOAD_MESSAGE', { totalSize: this.fileSizePipe.transform(totalSize) }),
                confirmText: this.translateService.instant('GENERAL.DOWNLOAD'),
            });

            confirmModal.result
                .then(() => {
                    if (this.selectedRows.length === 1) {
                        const { token, name, id } = this.selectedFiles[0];
                        this.fileDownloadService.nativeDownload(token, name, id);
                        return;
                    }
                    const fileIds = this.selectedFiles.map(({ id }: FileModel) => id);
                    this.fileDownloadService.nativeDownloadFromStream(fileIds, ARCHIVED_FILES_FILENAME);
                })
                .catch(() => {
                    // do nothing
                });
        } else {
            const fileIds = this.selectedFiles.map(({ id }: FileModel) => id);
            this.fileDownloadService.nativeDownloadFromStream(fileIds, ARCHIVED_FILES_FILENAME);
        }
    }

    onRowCheckboxChange(row: TableRow): void {
        row.selected = !row.selected;
        if (row.selected) {
            this.selectedRows.push(row);
        } else {
            this.selectedRows = this.selectedRows.filter(({ item }: TableRow) => item.id !== row.item.id);
        }
        this.setAllFilesSelected();
    }

    openArivisAnnotateView(row: TableRow): void {
        const url = this.filePreviewService.generateArivisMachineLearningViewerUrl(<FileModel>row.item, this.datasetId);
        this.windowRef.nativeWindow.open(url, '_blank');
    }

    handleRowClick(row: TableRow, event: Event): void {
        if (row.item.hasOwnProperty('convertStatus') && (row.item as FileModel).convertStatus === 'Active') {
            return; // we do not want to be able to select files that are currently under conversion
        }
        if (this.isCheckboxIncluded() && event.type === 'click') {
            this.onRowCheckboxChange(row);
        }
        if (event.type === 'click') {
            if (row.isFolder) {
                this.onClickOnFolder.emit(row.item.id);
            } else {
                this.onClickOnFile.emit(FileModel.createInstance(<FileModel>row.item));
            }
        }
    }

    onFileItemSubMenuOpen(index: number, isOpen: boolean): void {
        // in case of directly opening a submenu though another is still open, "open" second is sent before "close" first
        // hence this special "else if" is required to work
        if (isOpen) {
            this.fileItemSubMenuOpenIndex = index;
        } else if (this.fileItemSubMenuOpenIndex === index) {
            this.fileItemSubMenuOpenIndex = null;
        }
    }

    onFolderItemSubMenuOpen(index: number, isOpen: boolean): void {
        // NOTE: read comment in method `onFileItemSubMenuOpen()`
        if (isOpen) {
            this.folderItemSubMenuOpenIndex = index;
        } else if (this.folderItemSubMenuOpenIndex === index) {
            this.folderItemSubMenuOpenIndex = null;
        }
    }

    onCreateStudyId(row: TableRow<FileModel>, studyId: string): void {
        if (row) {
            this.changeStudyId.emit({ studyId: studyId, fileIDs: [row.item.id] });
        }
    }

    onCreateCustomTag(row: TableRow<FileModel | Folder>, customTag: string): void {
        if (row && row.item && (row.item as FileModel)) {
            let updatedCustomTags: string[];
            const metadata = (row.item as FileModel).metadata;

            if ('customTags' in metadata) {
                if (!metadata.customTags.includes(customTag) || metadata.customTags.length === 0) {
                    updatedCustomTags = [...(metadata.customTags || []), customTag];
                } else {
                    updatedCustomTags = metadata.customTags;
                }
            } else {
                updatedCustomTags = [customTag];
            }
            this.changeCustomTags.emit({ customTags: updatedCustomTags, fileIDs: [row.item.id] });
        }
    }

    onTagRemove(row: TableRow<FileModel>, customTag: string): void {
        if (row) {
            const updatedCustomTags = row.item.metadata.customTags.filter(tag => tag !== customTag);
            this.removeTag.emit({ customTags: updatedCustomTags, fileIDs: [row.item.id] });
        }
    }

    uploadCompleted(): void {
        this.onAllUploadsCompleted.emit();
    }

    uploadCancelled(): void {
        this.onUploadCancelled.emit();
    }

    isCheckboxIncluded(): boolean {
        return this.columns?.some(column => column.key === 'checkbox');
    }

    isFileStatusIncluded(): boolean {
        return this.columns?.some(column => column.key === 'fileStatus') && this.datasetFileType !== DatasetFileTypeOption.ANNOTATIONS;
    }

    isEmptyAndModificationMode(): boolean {
        return !this.isLoading && this.mode === FILE_EXPLORER_MODE.MODIFICATION && this.rows?.length === 0;
    }

    isRowFile(row: TableRow<FileModel | Folder | MedResultItemVM>): boolean {
        return !row.isFolder;
    }

    isPreviewBtnEnabled(item: FileModel): boolean {
        return this.actions.includes('view') && (this.arivisService.isSupportedByArivis(item.type) || this.arivisService.isSupportedByFilePreviewer(item.type));
    }

    isRenameBtnEnabled(): boolean {
        return this.actions.includes('rename');
    }

    isDownloadBtnEnabled(): boolean {
        return this.actions.includes('download');
    }

    isDeleteBtnEnabled(): boolean {
        return this.actions.includes('delete') && this.mode === FILE_EXPLORER_MODE.MODIFICATION;
    }

    isRemoveBtnEnabled(): boolean {
        return this.actions.includes('remove') && this.mode === FILE_EXPLORER_MODE.MODIFICATION;
    }

    isShareBtnEnabled(file: FileModel): boolean {
        return !this.isMEDProjectEnabled && this.actions.includes('share') && file && this.arivisService.isSupportedByArivis(file.type);
    }

    filesAdded(items: UploadFileItem[]): void {
        if (items.length > 0) {
            this.onFilesAdded.emit(items);
        }
    }

    editResearchStudyID(selectedFiles: string | number): void {
        const modal: NgbModalRef = this.modalService.open(MEDEditStudyIDDialogComponent, { size: 'md' });

        const editResearchStudyIDMEDDialogComponent = modal.componentInstance;
        editResearchStudyIDMEDDialogComponent.title = this.translateService.instant('MY_WORKSPACE.MY_DATA.EDIT_RESEARCH_ID_MODAL.EDIT_RESEARCH_ID_TITLE');
        editResearchStudyIDMEDDialogComponent.inputLabel = this.translateService.instant('MY_WORKSPACE.MY_DATA.PROVIDE_STUDY_ID');
        editResearchStudyIDMEDDialogComponent.cancelText = this.translateService.instant('MY_WORKSPACE.MY_DATA.CANCEL');
        editResearchStudyIDMEDDialogComponent.selectedFiles = selectedFiles;
        editResearchStudyIDMEDDialogComponent.selectedFileIDs = this.selectedRows.map(row => row.item.id);
        editResearchStudyIDMEDDialogComponent.hasStudyID = this.selectedRows.some(obj => (obj.item as FileModel).metadata?.studyId);
        editResearchStudyIDMEDDialogComponent.titleKey = 'studyId';
        editResearchStudyIDMEDDialogComponent.placeholder = 'Enter study ID';
        editResearchStudyIDMEDDialogComponent.confirmText = this.translateService.instant('MY_WORKSPACE.MY_DATA.EDIT_RESEARCH_ID_MODAL.APPLY_STUDY_ID');

        editResearchStudyIDMEDDialogComponent.applyStudyIdEvent.subscribe((data: { studyId: string; fileIDs: string[] }) => {
            const appliedStudyID: string = data.studyId;
            const fileIDs: string[] = data.fileIDs;
            this.changeStudyId.emit({ studyId: appliedStudyID, fileIDs: fileIDs });
        });
    }

    addTags(selectedFiles: string | number): void {
        const modal: NgbModalRef = this.modalService.open(MEDEditTagsDialogComponent, { size: 'md' });

        const addTagsMEDDialogComponent = modal.componentInstance;
        addTagsMEDDialogComponent.title = this.translateService.instant('MY_WORKSPACE.MY_DATA.ADD_TAGS_MODAL.ADD_TAGS_MODAL_TITLE');
        addTagsMEDDialogComponent.inputLabel = this.translateService.instant('MY_WORKSPACE.MY_DATA.PROVIDE_TAGS');
        addTagsMEDDialogComponent.cancelText = this.translateService.instant('MY_WORKSPACE.MY_DATA.CANCEL');
        addTagsMEDDialogComponent.selectedFiles = selectedFiles;
        addTagsMEDDialogComponent.selectedFileIDs = this.selectedRows.map(row => row.item.id);
        addTagsMEDDialogComponent.placeholder = 'Enter tags';
        addTagsMEDDialogComponent.titleKey = 'customTags';
        addTagsMEDDialogComponent.confirmText = this.translateService.instant('MY_WORKSPACE.MY_DATA.ADD_TAGS_MODAL.ADD_TAGS_MODAL_TITLE');

        addTagsMEDDialogComponent.applyTagsEvent.subscribe((data: { customTags: string[]; fileIDs: string[] }) => {
            const appliedTags = data.customTags;
            const fileIDs = data.fileIDs;

            this.changeCustomTags.emit({ customTags: appliedTags, fileIDs: fileIDs });
        });
    }

    private setAllFilesSelected(): void {
        this.areAllFilesSelected = this.selectedRows.length === this._rows?.length;
    }

    private getSelectedItemsText(selectedRows: TableRow[]): string {
        let text: string = '';
        const filesCount = (selectedRows && selectedRows.filter(i => !i.isFolder).length) || 0;
        const foldersCount = (selectedRows && selectedRows.filter(i => i.isFolder).length) || 0;
        if (foldersCount === 1 && filesCount > 1) {
            text += this.translateService.instant('MY_WORKSPACE.MY_FILES.FOLDER_AND_FILES_SELECTED', { filesCount });
            return text;
        }
        if (foldersCount > 1 && filesCount === 1) {
            text += this.translateService.instant('MY_WORKSPACE.MY_FILES.FOLDERS_AND_FILE_SELECTED', { foldersCount });
            return text;
        }
        if (foldersCount === 1 && filesCount === 1) {
            text += this.translateService.instant('MY_WORKSPACE.MY_FILES.FOLDER_AND_FILE_SELECTED');
            return text;
        }
        if (foldersCount > 1 && filesCount > 1) {
            text += this.translateService.instant('MY_WORKSPACE.MY_FILES.FOLDERS_AND_FILES_SELECTED', { foldersCount, filesCount });
            return text;
        }

        if (foldersCount > 0) {
            if (foldersCount > 1) {
                text += this.translateService.instant('MY_WORKSPACE.MY_FILES.FOLDERS_SELECTED', { foldersCount });
            } else {
                text += this.translateService.instant('MY_WORKSPACE.MY_FILES.FOLDER_SELECTED');
            }
            return text;
        }
        if (filesCount > 0) {
            if (filesCount > 1) {
                text += this.translateService.instant('MY_WORKSPACE.MY_FILES.FILES_SELECTED', { filesCount });
            } else {
                text += this.translateService.instant('MY_WORKSPACE.MY_FILES.FILE_SELECTED');
            }
            return text;
        }
        return text;
    }

    private getFiles(items: TableRow[]): FileModel[] {
        return items.filter(i => !i.isFolder).map(i => <FileModel>i.item);
    }
}
